import * as THREE from 'three';
import { DRUID_PATH } from '@somnhold/content';
import { gladeLayout, gladeUp, hexToWorld, NONE, streamLayout, thawLayout, type BattleState, type GladeLayout, type MapDef, type StreamLayout, type ThawLayout } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import { lookPoint, Precip, type OmenCtx } from '../omens/common.ts';
import { std } from '../materials.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The Circle's wood under the Grandsire (docs/sendas/druida.md, chapter 1, spring), for the druid's path: young green,
 * wet bark, moss, the melt water everywhere, a grey-green light through the rain (never a dark scene: spring daylight
 * under cloud). Each place:
 *
 * - `floodWood` (act 1, the thaw): the low wood on a slope, birch and alder trunks along it and the wood standing thick
 *   past its edges; the mounds (lomas: humps of earth held by roots, moss and a sapling on each, a pale rim on them
 *   while a surge is coming: the safe ground shows), the mud (glossy dark patches); the melt water as one sheet laid on
 *   the ground: the surge's wall coming down with its white foam at the front, the flood from below with its line;
 *   Olmo's clearing at the top in a brighter light. The rain over all of it.
 * - `olmoGlade` (act 2 and its scene): Olmo's clearing, round, the flood round it as still water with the trees
 *   standing in it; the old trunk at its north (where Ceda looks: the face shows little by little as her search goes,
 *   white when found); the new shoots in their ring (pale green, a glow), Hob's cane fences round the ones he did, the
 *   mud where one was eaten; the mud pools at the rim, bubbling.
 * - `stream` (act 3, the test): the bank at the foot (two mounds), the swollen stream across the board running east, the
 *   stones that show in it, the islet with the seed (black and shining on a knot of root), the far bank's wood. The two
 *   living roots grow from the bank stone to stone as the sim's tips do, in arcs over the water; one that braids twists
 *   round the other; a knot is a gnarl with a red ring; the sprouts on the stones; the Lodazal's bubbles at the sprout
 *   it goes for, its slap's red ring on the bank, the mud wave's band; the bridge braiding thick and green, and its flash.
 * - `groveLeaf`, `grove`, `groveYard` (the opening): the bastion's yard in the rain at dusk, the Grove where it stands
 *   (the scene's structure), the leaf of the Grandsire falling (black inside) and lying on the flags.
 * - `rootEave` (the campfire): under a roof of great roots, the rain falling past it, moss; night (the fire is the scene's).
 * - `olmoFace` (the close): Olmo's clearing at dusk in the fog, the face carved in the old trunk with its words.
 *
 * Draw calls: the dressing (one merged mesh), the water sheet (one; its wet parts from uniforms), the stream (one), the
 * roots (one instanced), the sprouts and fences (one instanced each), the rings (one instanced), the rain (one), the
 * glow layer, the sky dome. No lights of its own: the board's are repainted (HOURS).
 */

export const BOSQUE_PLACES = new Set(['floodWood', 'olmoGlade', 'stream', 'groveLeaf', 'grove', 'groveYard', 'rootEave', 'olmoFace']);

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const GREEN = new THREE.Color('#b8f08a');
const PALE = new THREE.Color('#e8f4e0');
const BARK = ['#4a3f36', '#3d352e', '#57493d'];
const BIRCH = ['#c8ccc8', '#b6bbb7'];
const LEAF = ['#7fae4c', '#93c25a', '#6a9a40', '#a6cf6c'];
const DARK_LEAF = ['#3f5c30', '#4b6a36', '#34502a'];
const MOSS = ['#5c7a3a', '#6b8a44', '#4e6a33'];
const STONE = ['#7c827e', '#6c726f', '#8a908b'];
const EARTH = ['#4a3e31', '#55473a', '#3f352b'];

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

/** The light by the hour: spring under rain clouds (grey-green, bright enough to read), dusk, night by the fire. */
const HOURS: Record<Hour, { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string] }> = {
  day: { bg: '#8b9894', fog: ['#8d9a95', 26, 105], hemi: ['#e2ece2', '#2e3828', 2.5], key: ['#f6f8f2', 2.6], rim: ['#bcd2ea', 1.3], sky: ['#66787a', '#b2c0ba', '#eef2ec'] },
  dawn: { bg: '#98a3a4', fog: ['#9aa5a6', 26, 105], hemi: ['#dfe6e6', '#2a3028', 2.3], key: ['#fff2ea', 2.6], rim: ['#b4c8ee', 1.4], sky: ['#63738a', '#c3ccd0', '#fff4ee'] },
  dusk: { bg: '#4d5a62', fog: ['#56636a', 22, 95], hemi: ['#c6d2dc', '#1e2420', 2.2], key: ['#e8e2ea', 2.0], rim: ['#9ab4f0', 1.6], sky: ['#232c3c', '#6a7888', '#d8c8d0'] },
  night: { bg: '#0b1012', fog: ['#10171a', 18, 70], hemi: ['#8798b0', '#0c0e0c', 1.25], key: ['#b2c2e0', 0.9], rim: ['#7a96d8', 1.0], sky: ['#03060a', '#101820', '#8fa4c8'] },
};

/** The melt water laid on the ground (a copy of the board's ground, lifted): wet where the sim's surge or flood is. */
const floodMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: { uTime: { value: 0 }, uFront: { value: -1e9 }, uLen: { value: 6 }, uLow: { value: 1e9 }, uOn: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec3 vW;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float uTime; uniform float uFront; uniform float uLen; uniform float uLow; uniform float uOn; varying vec3 vW;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float z = vW.z;
        // The surge: a wall going down (+z), its foam at the front; the flood from below, its line.
        float surge = uOn * step(uFront - uLen, z) * step(z, uFront);
        float flood = step(uLow, z);
        float wet = max(surge, flood);
        if (wet < 0.5) discard;
        float flow = n(vec2(vW.x * 0.9, z * 0.6 - uTime * (surge > 0.5 ? 3.2 : 0.6))) * 0.6 + n(vW.xz * 2.4 + uTime * 0.4) * 0.4;
        vec3 deep = vec3(0.16, 0.30, 0.34);
        vec3 col = mix(deep, vec3(0.38, 0.55, 0.58), flow * 0.8);
        // Foam: at the surge's front (wide, churning) and its tail; at the flood's line (thin).
        float front = surge * smoothstep(2.2, 0.0, uFront - z);
        float tail = surge * smoothstep(0.8, 0.0, z - (uFront - uLen)) * 0.5;
        float line = flood * smoothstep(0.5, 0.0, z - uLow);
        float foam = max(max(front, tail), line) * (0.55 + 0.45 * n(vec2(vW.x * 3.0 + uTime * 2.0, z * 3.0)));
        foam = max(foam, surge * smoothstep(0.78, 0.92, flow) * 0.5);
        col = mix(col, vec3(0.93, 0.96, 0.95), clamp(foam, 0.0, 1.0));
        gl_FragColor = vec4(col, 0.9 + foam * 0.1);
      }`,
  });

/** Running or still water: the stream (east, `uRun` > 0) or the clearing's flood (still). */
const runMat = (run: number): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: { uTime: { value: 0 }, uRun: { value: run } },
    vertexShader: /* glsl */ `
      varying vec3 vW;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float uTime; uniform float uRun; varying vec3 vW;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        vec2 p = vW.xz;
        float a = n(vec2(p.x * 0.7 - uTime * uRun * 1.6, p.y * 1.8)) * 0.6 + n(p * 2.2 + vec2(-uTime * uRun, uTime * 0.2)) * 0.4;
        vec3 col = mix(vec3(0.22, 0.30, 0.30), vec3(0.46, 0.56, 0.55), a);
        // Streaks of white where it runs fast.
        float streak = smoothstep(0.72, 0.86, n(vec2(p.x * 0.35 - uTime * uRun * 2.4, p.y * 5.0))) * uRun;
        col = mix(col, vec3(0.88, 0.93, 0.92), streak * 0.6);
        gl_FragColor = vec4(col, 0.86);
      }`,
  });

/** One root segment: a tapered cylinder of unit length along +Y (instanced, scaled and turned onto each piece). */
const rootGeo = (): THREE.BufferGeometry => new THREE.CylinderGeometry(0.5, 0.62, 1, 6, 1).translate(0, 0.5, 0);

export class BosqueLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(220);
  private dome: SkyDome | null = null;
  private rain: Precip | null = null;
  private terrain: TerrainBuild | null = null;
  private now = 0;
  private look = new THREE.Vector3();
  private outdoors = true;
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number }[] = [];
  private lastState: BattleState | null = null;
  // The thaw.
  private T: ThawLayout | null = null;
  private flood: THREE.ShaderMaterial | null = null;
  private floodMesh: THREE.Mesh | null = null;
  private moundAt: THREE.Vector3[] = [];
  private warnUntil = 0;
  // The clearing.
  private Gl: GladeLayout | null = null;
  private shoots: THREE.InstancedMesh | null = null;
  private fences: THREE.InstancedMesh | null = null;
  private faceMat: THREE.MeshStandardMaterial | null = null;
  private faceAt: THREE.Vector3 | null = null;
  private poolAt: THREE.Vector3[] = [];
  private bubble = 0;
  // The stream.
  private St: StreamLayout | null = null;
  private water: THREE.ShaderMaterial[] = [];
  private roots: THREE.InstancedMesh | null = null;
  private rootMat: THREE.MeshStandardMaterial | null = null;
  private sprouts: THREE.InstancedMesh | null = null;
  private rings: THREE.InstancedMesh | null = null;
  private nodeAt: THREE.Vector3[] = [];
  private mudBand: THREE.Mesh | null = null;
  private mudMat: THREE.MeshBasicMaterial | null = null;
  private bridgeFlash = 0;
  // The opening's leaf.
  private leaf: THREE.Group | null = null;
  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private c3 = new THREE.Color();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `bosque-${place}`;
    const kit = new Kit();
    if (place === 'floodWood') this.buildThaw(kit, s);
    else if (place === 'olmoGlade' || place === 'olmoFace') this.buildGlade(kit, s, place === 'olmoFace');
    else if (place === 'stream') this.buildStream(kit, s);
    else if (place === 'rootEave') this.buildEave(kit);
    else this.buildYard(kit, place === 'groveLeaf');
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.04, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'bosque-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    // The rain, everywhere outdoors.
    if (this.outdoors) {
      const low = ctx.low;
      this.rain = new Precip({ count: low ? 420 : 1100, box: [14, 10, 14], fall: 13, wind: [0.5, 0.25], size: [0.016, 0.42], color: '#d2dee6', opacity: 0.36 }, 47);
      this.group.add(this.rain.mesh);
    }
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  private light(p: THREE.Vector3, color: THREE.ColorRepresentation, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: new THREE.Color(color), size, a, seed: this.lights.length * 1.37, flick });
  }

  /** A tree of the spring wood at a point: a birch (pale) or an alder (dark bark), its crown of young leaves. */
  private tree(kit: Kit, p: THREE.Vector3, k: number, tall = 1, wet = false): void {
    const cyl = new THREE.CylinderGeometry(0.11, 0.17, 1, 7);
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    const birch = k > 0.62;
    const h = (2.4 + k * 1.6) * tall;
    const lean = (k - 0.5) * 0.12;
    kit.add(cyl, birch ? (BIRCH[Math.floor(k * 9) % 2] as string) : (BARK[Math.floor(k * 7) % 3] as string), place(p.x, p.y + h * 0.5, p.z, k * 6, new THREE.Vector3(birch ? 0.8 : 1.1, h, birch ? 0.8 : 1.1), lean, -lean));
    // Birch's dark marks; the alder's roots at its foot.
    if (birch) for (let i = 0; i < 3; i++) kit.add(cyl, '#3a3a38', place(p.x + lean * (0.4 + i * 0.5), p.y + 0.5 + i * 0.6 * tall, p.z, k * 6, new THREE.Vector3(0.84, 0.05, 0.84)));
    else for (let i = 0; i < 3; i++) kit.add(cyl, BARK[1] as string, place(p.x + Math.cos(i * 2.1 + k) * 0.2, p.y + 0.06, p.z + Math.sin(i * 2.1 + k) * 0.2, i * 2.1, new THREE.Vector3(0.5, 0.45, 0.5), 1.2, 0));
    // Its crown: a few lumps of young leaf, lighter on top.
    const top = p.y + h;
    for (let i = 0; i < 5; i++) {
      const a = i * 1.7 + k * 4;
      const r = 0.45 + (i % 2) * 0.25;
      const lc = (wet ? DARK_LEAF : LEAF)[(i + Math.floor(k * 5)) % (wet ? 3 : 4)] as string;
      kit.add(ico, lc, place(p.x + lean * h + Math.cos(a) * r * 0.6, top - 0.2 + (i % 3) * 0.3, p.z + Math.sin(a) * r * 0.6, a, (0.9 + (i % 2) * 0.35) * (0.85 + k * 0.4)));
    }
    if (wet) kit.add(cyl, '#2a3428', place(p.x, p.y + 0.03, p.z, 0, new THREE.Vector3(3.0, 0.03, 3.0)));
    cyl.dispose();
    ico.dispose();
  }

  /** A mound (loma): a hump of earth held by roots, moss on it, a sapling or a mossy stone. */
  private mound(kit: Kit, cells: { q: number; r: number }[], k: number): THREE.Vector3 {
    const sph = new THREE.SphereGeometry(0.5, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    const cyl = new THREE.CylinderGeometry(0.06, 0.09, 1, 5);
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    const mid = new THREE.Vector3();
    for (const h of cells) {
      const p = this.hex(h.q, h.r);
      mid.add(p);
      kit.add(sph, EARTH[(h.q + h.r + 9) % 3] as string, place(p.x, p.y - 0.02, p.z, k * 3, new THREE.Vector3(1.9 * S, 0.62, 1.8 * S)));
      kit.add(sph, MOSS[(h.q * 3 + h.r + 9) % 3] as string, place(p.x + 0.05, p.y + 0.02, p.z - 0.05, k * 5, new THREE.Vector3(1.6 * S, 0.62, 1.5 * S)));
    }
    mid.multiplyScalar(1 / cells.length);
    // Roots clutching its side.
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + k;
      kit.add(cyl, BARK[i % 3] as string, place(mid.x + Math.cos(a) * 0.75, mid.y + 0.12, mid.z + Math.sin(a) * 0.75, a, new THREE.Vector3(1, 0.9, 1), Math.PI / 2 - 0.5, 0));
    }
    // A sapling on top, or a mossy stone.
    if (k > 0.45) {
      kit.add(cyl, BIRCH[0] as string, place(mid.x, mid.y + 0.7, mid.z, 0, new THREE.Vector3(0.7, 1.1, 0.7)));
      for (let i = 0; i < 3; i++) kit.add(ico, LEAF[i] as string, place(mid.x + Math.cos(i * 2) * 0.14, mid.y + 1.25 + i * 0.1, mid.z + Math.sin(i * 2) * 0.14, i, 0.42));
    } else {
      kit.add(ico, STONE[1] as string, place(mid.x + 0.1, mid.y + 0.42, mid.z, k * 4, new THREE.Vector3(0.6, 0.38, 0.5)));
      kit.add(ico, MOSS[0] as string, place(mid.x + 0.1, mid.y + 0.55, mid.z, k * 4, new THREE.Vector3(0.5, 0.15, 0.42)));
    }
    sph.dispose();
    cyl.dispose();
    ico.dispose();
    return mid.setY(mid.y + 0.62);
  }

  /** Ferns and grass about a point (small, many, merged). */
  private ferns(kit: Kit, p: THREE.Vector3, n: number, seed: number, color = MOSS): void {
    const cone = new THREE.ConeGeometry(0.06, 0.32, 4);
    for (let i = 0; i < n; i++) {
      const a = seed * 3 + i * 2.39;
      const d = 0.25 + ((i * 37 + seed) % 10) * 0.07;
      kit.add(cone, color[i % color.length] as string, place(p.x + Math.cos(a) * d, p.y + 0.12, p.z + Math.sin(a) * d, a, new THREE.Vector3(1, 0.9 + (i % 3) * 0.25, 0.5), 0.35 * Math.cos(a), 0.35 * Math.sin(a)));
    }
    cone.dispose();
  }

  /** The wood standing thick on rock cells (round the slope, past the clearing, the far bank). */
  private woodOn(kit: Kit, cells: { q: number; r: number }[], every: number, wet = false): void {
    cells.forEach((h, i) => {
      const k = Math.sin(h.q * 12.9 + h.r * 78.2) * 0.5 + 0.5;
      if ((h.q * 7 + h.r * 13 + 99) % every !== 0) return;
      const p = this.hex(h.q, h.r);
      p.x += (k - 0.5) * 0.4;
      this.tree(kit, p, k, 0.9 + ((i * 7) % 5) * 0.12, wet);
    });
  }

  /* ---------- the thaw ---------- */

  private buildThaw(kit: Kit, s: BattleState): void {
    const L = thawLayout(s.seed);
    this.T = L;
    // The wood along the slope: trunks on the way's trunk cells; the thick wood round it.
    // (Low crowns by the way, the camera looks over them; the wood thickens further out.)
    for (const t of L.trunks) {
      const k = Math.sin(t.q * 3.1 + t.r * 7.7) * 0.5 + 0.5;
      this.tree(kit, this.hex(t.q, t.r), k, 0.7);
    }
    const open = new Set(L.floor.map((h) => `${h.q},${h.r}`));
    const near = (h: { q: number; r: number }, d: number): boolean => {
      for (let dq = -d; dq <= d; dq++) for (let dr = -d; dr <= d; dr++) if (Math.abs(dq + dr) <= d && open.has(`${h.q + dq},${h.r + dr}`)) return true;
      return false;
    };
    const wood = L.rocks.filter((h) => !L.trunks.some((t) => t.q === h.q && t.r === h.r));
    wood.forEach((h, i) => {
      const k = Math.sin(h.q * 12.9 + h.r * 78.2) * 0.5 + 0.5;
      const edge = near(h, 1);
      const mid = !edge && near(h, 2);
      if (edge ? (h.q * 7 + h.r * 13 + 99) % 4 !== 0 : (h.q * 7 + h.r * 13 + 99) % 2 !== 0) return;
      const p = this.hex(h.q, h.r);
      p.x += (k - 0.5) * 0.4;
      this.tree(kit, p, k, edge ? 0.55 : mid ? 0.8 : 1 + ((i * 7) % 5) * 0.1);
    });
    // The mounds.
    L.mounds.forEach((m, i) => this.moundAt.push(this.mound(kit, m, (i * 0.37) % 1)));
    // The mud: glossy dark patches, a few reeds.
    const disc = new THREE.CylinderGeometry(0.5, 0.5, 0.04, 9);
    for (const patch of L.mud) for (const h of patch) {
      const p = this.hex(h.q, h.r);
      kit.add(disc, '#2e261f', place(p.x, p.y + 0.02, p.z, h.q, new THREE.Vector3(1.9 * S, 1, 1.7 * S)));
      kit.add(disc, '#3b3128', place(p.x + 0.15, p.y + 0.035, p.z - 0.1, h.r, new THREE.Vector3(1.0 * S, 1, 0.8 * S)));
    }
    disc.dispose();
    // Ferns and fallen leaves of last year along the floor.
    for (const h of L.floor) if ((h.q * 5 + h.r * 11 + 77) % 4 === 0) this.ferns(kit, this.hex(h.q, h.r), 4, h.q * 3 + h.r);
    // Olmo's clearing at the top: brighter, the old tree with its first face, a ring of stones.
    const g = this.ctx.at(L.glade.x, L.glade.y, 0);
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2;
      kit.add(ico, STONE[i % 3] as string, place(g.x + Math.cos(a) * 2.2, g.y + 0.12, g.z + Math.sin(a) * 1.6, a, new THREE.Vector3(0.5, 0.35, 0.45)));
    }
    this.tree(kit, g.clone().add(new THREE.Vector3(0, 0, -2.2)), 0.2, 1.6);
    this.light(g.clone().setY(g.y + 3), '#eef8e0', 7, 0.35);
    ico.dispose();
    // The melt coming over the top: a cascade of white down rocks above the clearing.
    const top = this.ctx.at(L.glade.x, L.glade.y - 3600, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (let i = 0; i < 6; i++) kit.add(box, STONE[i % 3] as string, place(top.x - 2.5 + i, top.y + 0.6 + (i % 2) * 0.5, top.z - 0.3 * (i % 3), i, new THREE.Vector3(1.1, 1.2 + (i % 3) * 0.4, 0.9)));
    box.dispose();
  }

  /* ---------- Olmo's clearing ---------- */

  private buildGlade(kit: Kit, s: BattleState, close: boolean): void {
    const L = gladeLayout(s.seed, DRUID_PATH.glade.shoots);
    this.Gl = L;
    const R = L.radius;
    // The flood round the clearing, still water with the trees standing in it.
    this.woodOn(kit, L.flood.filter((h) => Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(h.q + h.r)) >= R + 1), 2, true);
    const ring = this.track(new THREE.RingGeometry((R - 0.55) * S, (R + 9) * S, 48, 1).rotateX(-Math.PI / 2));
    const still = this.track(runMat(0));
    this.water.push(still);
    const pond = new THREE.Mesh(ring, still);
    pond.position.y = this.ctx.ground(0, 0) + 0.06;
    pond.renderOrder = 2;
    pond.name = 'bosque-pond';
    this.group.add(pond);
    // The old trunk at the north: huge, its roots out on the moss, the place for the face.
    const t = this.hex(0, -5);
    const cyl = new THREE.CylinderGeometry(0.5, 0.62, 1, 10);
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    kit.add(cyl, BARK[0] as string, place(t.x + 0.3, t.y + 2.6, t.z, 0.3, new THREE.Vector3(2.6, 5.2, 2.4)));
    kit.add(cyl, BARK[2] as string, place(t.x + 0.25, t.y + 0.9, t.z + 0.1, 0.9, new THREE.Vector3(2.9, 1.8, 2.7)));
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2 + 0.2;
      kit.add(cyl, BARK[i % 3] as string, place(t.x + 0.3 + Math.cos(a) * 1.4, t.y + 0.2, t.z + Math.sin(a) * 1.3, a, new THREE.Vector3(0.55, 1.6, 0.55), Math.PI / 2 - 0.4, 0));
    }
    for (let i = 0; i < 9; i++) {
      const a = i * 0.7;
      kit.add(ico, (i % 2 ? LEAF : DARK_LEAF)[i % 3] as string, place(t.x + 0.3 + Math.cos(a) * 1.8, t.y + 5.3 + (i % 3) * 0.6, t.z + Math.sin(a) * 1.4, a, 2.0 + (i % 3) * 0.5));
    }
    // Moss up its side.
    for (let i = 0; i < 6; i++) kit.add(ico, MOSS[i % 3] as string, place(t.x - 0.9 + (i % 3) * 0.4, t.y + 0.5 + i * 0.4, t.z + 1.0, i, new THREE.Vector3(0.5, 0.3, 0.3)));
    // The face, on the trunk's south side: a carved oval, its eyes and mouth (white when found, the scene's close).
    const faceAt = new THREE.Vector3(t.x + 0.3, t.y + 2.45, t.z + 1.42);
    this.faceAt = faceAt;
    // (Pale worn wood a shade off the bark, smaller than the trunk's face and flush with it: a carving, not a mask.)
    const fm = this.track(std('#8e7a66', { roughness: 0.95, emissive: '#000000', flatShading: true }));
    this.faceMat = fm;
    const fg = new THREE.Group();
    fg.position.copy(faceAt);
    const oval = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.42, 12, 8)), fm);
    oval.scale.set(1.15, 1.5, 0.22);
    fg.add(oval);
    const carve = this.track(std('#2a1a12', { roughness: 1 }));
    // Its brow and nose, left standing in the cut.
    const ridge = this.track(std('#9c8872', { roughness: 0.95, flatShading: true }));
    const brow = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.42, 0.05, 0.07)), ridge);
    brow.position.set(0, 0.3, 0.08);
    const nose = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.06, 0.2, 0.07)), ridge);
    nose.position.set(0, 0.02, 0.09);
    fg.add(brow, nose);
    for (const k of [-1, 1]) {
      const eye = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.07, 8, 6)), carve);
      eye.position.set(k * 0.17, 0.17, 0.08);
      eye.scale.set(1.3, 0.6, 0.5);
      fg.add(eye);
    }
    const mouth = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.22, 0.04, 0.05)), carve);
    mouth.position.set(0, -0.26, 0.08);
    mouth.scale.set(1.2, 1, 1);
    fg.add(mouth);
    // The words under it (three short grooves: the scene says them).
    for (let i = 0; i < 3; i++) {
      const w = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.42 - i * 0.08, 0.025, 0.03)), carve);
      w.position.set(0, -0.62 - i * 0.09, 0.05);
      fg.add(w);
    }
    fg.name = 'bosque-face';
    this.group.add(fg);
    if (close) fm.emissive.set('#2a2418');
    // The ring of stones and the moss of the clearing; ferns round its rim.
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * Math.PI * 2;
      const p = this.ctx.at(Math.cos(a) * (R - 1.1) * 1000, Math.sin(a) * (R - 1.1) * 1000, 0);
      if (i % 2) this.ferns(kit, p, 5, i);
      else kit.add(ico, STONE[i % 3] as string, place(p.x, p.y + 0.12, p.z, a, new THREE.Vector3(0.5, 0.32, 0.42)));
    }
    // The mud pools at the rim: dark glossy water, a lip of mud.
    const disc = new THREE.CylinderGeometry(0.5, 0.5, 0.05, 12);
    for (const h of L.pools) {
      const p = this.hex(h.q, h.r);
      this.poolAt.push(p.clone());
      kit.add(disc, '#3d3127', place(p.x, p.y + 0.02, p.z, 0, new THREE.Vector3(2.3, 1, 2.0)));
      kit.add(disc, '#241d17', place(p.x, p.y + 0.04, p.z, 0, new THREE.Vector3(1.6, 1, 1.4)));
      for (let i = 0; i < 4; i++) kit.add(ico, EARTH[i % 3] as string, place(p.x + Math.cos(i * 1.6) * 1.0, p.y + 0.08, p.z + Math.sin(i * 1.6) * 0.9, i, new THREE.Vector3(0.4, 0.2, 0.35)));
    }
    disc.dispose();
    cyl.dispose();
    ico.dispose();
    // The shoots and their fences (instanced): a shoot of new leaves on each, the canes round the fenced ones.
    const n = L.shoots.length;
    const sg = this.track(new THREE.ConeGeometry(0.16, 0.55, 5).translate(0, 0.27, 0));
    const sm = this.track(std('#a8e070', { roughness: 0.7, emissive: '#3a6a1c', emissiveIntensity: 0.6 }));
    const shoots = new THREE.InstancedMesh(sg, sm, n * 3);
    shoots.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 3 * 3), 3);
    shoots.name = 'bosque-shoots';
    shoots.frustumCulled = false;
    this.shoots = shoots;
    this.group.add(shoots);
    const fg2 = this.track(new THREE.CylinderGeometry(0.025, 0.03, 0.8, 4).translate(0, 0.4, 0));
    const fm2 = this.track(std('#a89a68', { roughness: 0.9 }));
    fm2.color.set('#8a9a62');
    const fences = new THREE.InstancedMesh(fg2, fm2, n * 8);
    fences.name = 'bosque-fences';
    fences.frustumCulled = false;
    this.fences = fences;
    this.group.add(fences);
  }

  /* ---------- the stream ---------- */

  private buildStream(kit: Kit, s: BattleState): void {
    const L = streamLayout(s.seed, DRUID_PATH.roots.see);
    this.St = L;
    const nodes = L.net.nodes;
    this.nodeAt = nodes.map((h) => this.hex(h.q, h.r));
    // The far bank's wood, and trees along both ends of the bank.
    this.woodOn(kit, L.far, 2, false);
    // Ferns on the bank; trees only past its sides (out of the way of the hero and the stones).
    for (const h of L.bank) if ((h.q * 5 + h.r * 11 + 77) % 5 === 0) this.ferns(kit, this.hex(h.q, h.r), 4, h.q + h.r * 3);
    const bankRow = Math.max((L.net.nodes[0] as { r: number }).r, (L.net.nodes[1] as { r: number }).r);
    for (let r = bankRow - 1; r <= bankRow + 3; r++)
      for (let q = -L.radius - 3; q <= L.radius + 3; q++) {
        const d = Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
        if (d <= L.radius || d > L.radius + 2 || (q * 7 + r * 13 + 99) % 2 !== 0) continue;
        this.tree(kit, this.hex(q, r), Math.sin(q * 3.1 + r) * 0.5 + 0.5, 1.05);
      }
    // The bank's mounds.
    L.mounds.forEach((m, i) => this.moundAt.push(this.mound(kit, m, 0.2 + i * 0.5)));
    // The stream: a sheet of running water over its channel.
    const water = runMat(1);
    this.track(water);
    this.water.push(water);
    const top = (nodes[2] as { r: number }).r - 1;
    const bottom = Math.max((nodes[0] as { r: number }).r, (nodes[1] as { r: number }).r);
    const z0 = (top - 0.6) * 866 * K;
    const z1 = (bottom - 0.45) * 866 * K;
    const plane = new THREE.Mesh(this.track(new THREE.PlaneGeometry(60, z1 - z0).rotateX(-Math.PI / 2)), water);
    plane.position.set(0, this.ctx.ground(0, (z0 + z1) / 2) + 0.03, (z0 + z1) / 2);
    plane.renderOrder = 2;
    plane.name = 'bosque-stream';
    this.group.add(plane);
    // The bank's lip: stones and roots along the water's edge.
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    for (let x = -9; x <= 9; x += 0.8) {
      const z = z1 + 0.05;
      kit.add(ico, (Math.floor(x * 3) % 2 ? STONE : EARTH)[Math.abs(Math.floor(x * 7)) % 3] as string, place(x, this.ctx.ground(x, z) + 0.05, z, x, new THREE.Vector3(0.55, 0.3, 0.4)));
    }
    // The stones in the stream: flat-topped, mossy, raised over the water (the roots' bank ends are root boles).
    const cyl = new THREE.CylinderGeometry(0.5, 0.6, 1, 8);
    nodes.forEach((h, i) => {
      const p = this.nodeAt[i] as THREE.Vector3;
      if (i < 2) {
        // A living root's bole on the bank: a great root out of the ground, its tip toward the water.
        kit.add(cyl, BARK[0] as string, place(p.x, p.y + 0.35, p.z + 0.25, 0.3, new THREE.Vector3(1.1, 0.7, 1.0)));
        kit.add(ico, MOSS[1] as string, place(p.x, p.y + 0.72, p.z + 0.25, 0, new THREE.Vector3(0.9, 0.3, 0.8)));
        return;
      }
      if (i === 2) {
        // The islet: a rocky island, grass on it, the seed on a knot of root in its middle.
        kit.add(cyl, STONE[1] as string, place(p.x, p.y + 0.1, p.z, 0.2, new THREE.Vector3(3.2, 0.55, 2.8)));
        kit.add(cyl, MOSS[0] as string, place(p.x, p.y + 0.4, p.z, 0.5, new THREE.Vector3(2.7, 0.12, 2.3)));
        for (let k = 0; k < 6; k++) kit.add(ico, STONE[k % 3] as string, place(p.x + Math.cos(k) * 1.3, p.y + 0.35, p.z + Math.sin(k) * 1.1, k, new THREE.Vector3(0.6, 0.45, 0.5)));
        kit.add(cyl, BARK[1] as string, place(p.x, p.y + 0.62, p.z, 0, new THREE.Vector3(0.6, 0.35, 0.6)));
        this.light(p.clone().setY(p.y + 1.1), '#e0f0ff', 1.6, 0.8, 1);
        this.ferns(kit, p.clone().setY(p.y + 0.38), 7, 3);
        return;
      }
      void h;
      kit.add(cyl, STONE[i % 3] as string, place(p.x, p.y + 0.05, p.z, i, new THREE.Vector3(1.55 * S, 0.5, 1.4 * S)));
      kit.add(ico, MOSS[i % 3] as string, place(p.x - 0.15, p.y + 0.3, p.z + 0.1, i, new THREE.Vector3(0.7, 0.1, 0.55)));
    });
    // The seed on the islet: black and shining.
    const isle = this.nodeAt[2] as THREE.Vector3;
    const seed = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.2, 12, 8)), this.track(std('#0c0c10', { roughness: 0.12, metalness: 0.6, emissive: '#1a2620', emissiveIntensity: 0.6 })));
    seed.position.set(isle.x, isle.y + 0.95, isle.z);
    seed.scale.set(1, 1.3, 1);
    seed.name = 'bosque-seed';
    this.group.add(seed);
    cyl.dispose();
    ico.dispose();
    // The roots (instanced pieces), the sprouts on the stones, the rings (knot, slap).
    const rm = this.track(std('#ffffff', { roughness: 0.8, emissive: '#2e4a1c', emissiveIntensity: 0.3, flatShading: true }));
    this.rootMat = rm;
    const roots = new THREE.InstancedMesh(this.track(rootGeo()), rm, 160);
    roots.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(160 * 3), 3);
    roots.count = 0;
    roots.frustumCulled = false;
    roots.castShadow = true;
    roots.name = 'bosque-roots';
    this.roots = roots;
    this.group.add(roots);
    const sg = this.track(new THREE.ConeGeometry(0.15, 0.5, 5).translate(0, 0.25, 0));
    const sm = this.track(std('#a8e070', { roughness: 0.7, emissive: '#3a6a1c', emissiveIntensity: 0.6 }));
    const sprouts = new THREE.InstancedMesh(sg, sm, nodes.length * 3);
    sprouts.count = 0;
    sprouts.frustumCulled = false;
    sprouts.name = 'bosque-sprouts';
    this.sprouts = sprouts;
    this.group.add(sprouts);
    const rg = this.track(new THREE.RingGeometry(0.86, 1, 40).rotateX(-Math.PI / 2));
    const rmat = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.85, depthWrite: false, side: THREE.DoubleSide }));
    const rings = new THREE.InstancedMesh(rg, rmat, 8);
    rings.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(8 * 3), 3);
    rings.count = 0;
    rings.frustumCulled = false;
    rings.renderOrder = 5;
    rings.name = 'bosque-rings';
    this.rings = rings;
    this.group.add(rings);
    // The mud wave's band over the bank (shown while it's coming).
    const mb = this.track(new THREE.PlaneGeometry(60, 4.2).rotateX(-Math.PI / 2));
    const mmat = this.track(new THREE.MeshBasicMaterial({ color: '#5a4430', transparent: true, opacity: 0, depthWrite: false }));
    this.mudMat = mmat;
    const band = new THREE.Mesh(mb, mmat);
    band.position.set(0, this.ctx.ground(0, z1 + 2) + 0.07, z1 + 2.1);
    band.renderOrder = 4;
    band.visible = false;
    this.mudBand = band;
    this.group.add(band);
  }

  /* ---------- the scenes ---------- */

  /** The bastion's yard in the rain at dusk: wet flags, a few trees, the Grove where the scene puts it; the leaf. */
  private buildYard(kit: Kit, falling: boolean): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    for (let q = -6; q <= 6; q++)
      for (let r = -6; r <= 6; r++) {
        if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) > 6) continue;
        const p = this.hex(q, r);
        for (let k = 0; k < 3; k++) {
          const a = k * 2.1 + q;
          kit.add(box, ['#5d6463', '#535a5a', '#666d6b'][(q + r + k + 12) % 3] as string, place(p.x + Math.cos(a) * 0.28, p.y + 0.012, p.z + Math.sin(a) * 0.26, (q + r) * 0.3 + k, new THREE.Vector3(0.42, 0.025, 0.3)));
        }
        if ((q * 5 + r * 3 + 30) % 4 === 0) this.ferns(kit, p, 3, q * 3 + r, MOSS);
      }
    box.dispose();
    for (const [q, r, k] of [
      [-4, 1, 0.3],
      [4, -2, 0.7],
      [-3, -3, 0.8],
      [3, 3, 0.2],
    ] as const)
      this.tree(kit, this.hex(q, r), k, 1.1);
    // The leaf of the Grandsire: green on its face, black from the inside out along its veins.
    const leaf = new THREE.Group();
    const blade = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.28, 12, 8)), this.track(std('#5f8a3c', { roughness: 0.7, side: THREE.DoubleSide })));
    blade.scale.set(0.6, 0.06, 1.2);
    leaf.add(blade);
    const rot = this.track(std('#121014', { roughness: 0.4, metalness: 0.2 }));
    const mid = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.03, 0.03, 0.62)), rot);
    mid.position.y = 0.02;
    leaf.add(mid);
    for (let i = 0; i < 4; i++)
      for (const k of [-1, 1]) {
        const v = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.02, 0.025, 0.2)), rot);
        v.position.set(k * 0.07, 0.02, -0.18 + i * 0.12);
        v.rotation.y = k * 0.7;
        leaf.add(v);
      }
    const blot = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.1, 8, 6)), rot);
    blot.scale.set(1, 0.3, 1.6);
    blot.position.y = 0.01;
    leaf.add(blot);
    leaf.name = 'bosque-leaf';
    const g = this.ctx.at(300, 500, 0.02);
    leaf.position.copy(g);
    if (!falling) leaf.rotation.set(0, 0.6, 0.08);
    this.leaf = falling ? leaf : null;
    this.group.add(leaf);
  }

  /** The campfire under a roof of great roots, the rain falling past it, moss. */
  private buildEave(kit: Kit): void {
    const cyl = new THREE.CylinderGeometry(0.22, 0.32, 1, 7);
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    // Arches of root over the fire.
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI - Math.PI / 2;
      for (let k = 0; k < 6; k++) {
        const t = k / 5;
        const x = Math.cos(a) * (3.2 - Math.sin(t * Math.PI) * 0.3);
        const z = -1.2 + (t - 0.5) * 5.5;
        const y = Math.sin(t * Math.PI) * 3.0;
        kit.add(cyl, BARK[(i + k) % 3] as string, place(x * (1 - Math.sin(t * Math.PI) * 0.55), y, z, a, new THREE.Vector3(1, 1.2, 1), 0.9 * (t - 0.5) * 2, 0));
      }
    }
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2;
      kit.add(ico, MOSS[i % 3] as string, place(Math.cos(a) * 2.6, 0.12, Math.sin(a) * 2.2 - 0.4, a, new THREE.Vector3(0.8, 0.3, 0.6)));
    }
    for (const [x, z, k] of [
      [-4, -3, 0.2],
      [4, -2.5, 0.7],
      [-4.5, 2, 0.5],
      [4.2, 2.5, 0.9],
    ] as const)
      this.tree(kit, new THREE.Vector3(x, this.ctx.ground(x, z), z), k, 1.2, true);
    cyl.dispose();
    ico.dispose();
  }

  /* ---------- painting ---------- */

  apply(terrain: TerrainBuild, hour: Hour = 'day'): void {
    this.terrain = terrain;
    const c = new THREE.Color();
    const col = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    const water = new Set<string>();
    const stream = this.St;
    if (stream) for (const h of stream.water) water.add(`${h.q},${h.r}`);
    const mud = new Set<string>();
    if (this.T) for (const p of this.T.mud) for (const h of p) mud.add(`${h.q},${h.r}`);
    if (col && pos) {
      terrain.mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      for (let i = 0; i < col.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const k = 0.5 + 0.5 * Math.sin(v.x * 1.3 + v.z * 0.7) * Math.cos(v.z * 1.1 - v.x * 0.4);
        const hq = Math.round(v.x / S - v.z / (S * 0.866) / 2);
        const hr = Math.round(v.z / (S * 0.866));
        const key = `${hq},${hr}`;
        if (this.place === 'groveLeaf' || this.place === 'grove' || this.place === 'groveYard') c.set(['#3e4a3c', '#465242', '#3a4436'][Math.floor(k * 2.99)] as string).multiplyScalar(0.85 + lum * 0.3);
        else if (water.has(key)) c.set('#2c3532');
        else if (mud.has(key)) c.set('#3b3229');
        else c.set((k > 0.55 ? MOSS : ['#4e5a36', '#58663a', '#465230'])[Math.floor(k * 2.99) % 3] as string).multiplyScalar(0.82 + lum * 0.35);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    terrain.decor.traverse((o) => {
      if (o.name === 'roads') o.visible = false;
      if (o.name === 'outskirts-ground') {
        const g = (o as THREE.Mesh).geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
        if (g) {
          for (let i = 0; i < g.count; i++) {
            const lum = g.getX(i) * 0.3 + g.getY(i) * 0.59 + g.getZ(i) * 0.11;
            c.set('#3c4a2c').multiplyScalar(0.75 + lum * 0.4);
            g.setXYZ(i, c.r, c.g, c.b);
          }
          g.needsUpdate = true;
        }
      }
      // The board's own trees and rocks give way to the wood's.
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    // The melt water: a copy of the ground, lifted a little, wet where the sim says.
    if (this.T) {
      const fm = this.track(floodMat());
      this.flood = fm;
      const geo = this.track(terrain.mesh.geometry.clone());
      const mesh = new THREE.Mesh(geo, fm);
      mesh.position.copy(terrain.mesh.position);
      mesh.quaternion.copy(terrain.mesh.quaternion);
      mesh.scale.copy(terrain.mesh.scale);
      mesh.position.y += 0.07;
      mesh.renderOrder = 3;
      mesh.name = 'bosque-flood';
      (fm.uniforms.uLen as THREE.IUniform<number>).value = DRUID_PATH.thaw.surge.len * K;
      this.floodMesh = mesh;
      this.group.add(mesh);
    }
    if (terrain.fog) terrain.fog.group.visible = true;
    this.dome = new SkyDome();
    this.group.add(this.dome.mesh);
    this.paint(hour);
  }

  paint(hour: Hour, weather?: string): void {
    const look = HOURS[hour] ?? HOURS.day;
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = weather === 'fog' ? look.fog[1] * 0.6 : look.fog[1];
      scene.fog.far = weather === 'fog' ? look.fog[2] * 0.55 : look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(0.4, hour === 'dusk' ? 0.1 : 0.5, 0.3);
    this.terrain?.fog?.tint({ low: look.fog[0], mid: look.fog[0], far: look.bg, alpha: 1 });
    // A fog scene: no rain, only the mist.
    if (this.rain) this.rain.mesh.visible = weather !== 'fog';
  }

  /* ---------- each tick ---------- */

  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.2);
      if (e.kind === 'surgeWarn') {
        // The roar at the top: spray over the cascade, the mounds' rims lit.
        this.warnUntil = this.now + 6;
        this.ctx.shake(0.15);
        for (let i = 0; i < 5; i++) this.ctx.vfx.burst(this.ctx.at(e.x - 3000 + i * 1500, e.y - 1500, 0.8), { n: 10, color: '#eef4f2', speed: 2, up: 2.2, size: 0.35, life: 18, smoke: true });
      } else if (e.kind === 'surge') {
        this.ctx.shake(0.3);
      } else if (e.kind === 'wake') {
        this.ctx.vfx.burst(p, { n: 16, color: '#4a3b2e', speed: 1.8, up: 1.4, size: 0.2, life: 12, gravity: 1 });
      } else if (e.kind === 'glade' || e.kind === 'bridge') {
        this.ctx.flare(p.setY(p.y + 1.2), '#eaffd8', 4, 20);
        this.ctx.vfx.burst(p, { n: 30, color: '#b8f08a', speed: 2.4, up: 2.6, size: 0.16, life: 26 });
        if (e.kind === 'bridge') this.bridgeFlash = 1;
      } else if (e.kind === 'fence') {
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.05), 0.2, 1.0, '#e8f4e0', 6, 0.7);
      } else if (e.kind === 'eat') {
        this.ctx.vfx.burst(p, { n: 14, color: '#4a3b2e', speed: 1.6, up: 1.2, size: 0.18, life: 12, gravity: 1 });
        this.ctx.vfx.burst(p, { n: 8, color: '#a8e070', speed: 1.2, up: 1.6, size: 0.1, life: 14, gravity: 1 });
      } else if (e.kind === 'sow') {
        this.ctx.vfx.burst(p, { n: 10, color: '#b8f08a', speed: 1.2, up: 1.8, size: 0.12, life: 14 });
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.3), 0.2, 1.1, '#e8f4e0', 6, 0.8);
      } else if (e.kind === 'reach' || e.kind === 'home') {
        this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.4), { n: 8, color: '#c8f0a0', speed: 1, up: 1.2, size: 0.12, life: 12 });
      } else if (e.kind === 'tangle') {
        this.ctx.shake(0.25);
        this.ctx.flare(this.ctx.at(e.x, e.y, 0.6), '#ff4f3a', 2, 10);
        this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.5), { n: 14, color: '#3a2e24', speed: 1.6, up: 1.4, size: 0.14, life: 12, gravity: 1 });
      } else if (e.kind === 'knotCut') {
        this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.5), { n: 18, color: '#6a5642', speed: 2, up: 1.6, size: 0.12, life: 14, gravity: 1 });
      } else if (e.kind === 'braid') {
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.4), 0.3, 1.4, '#c8f0a0', 8, 0.8);
      } else if (e.kind === 'bubble') {
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.06), 0.3, 1.5, '#ff8a6e', 10, 0.8);
      } else if (e.kind === 'gulp') {
        this.ctx.shake(0.2);
        this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.3), { n: 22, color: '#4a3b2e', speed: 2.2, up: 2.2, size: 0.22, life: 14, gravity: 1 });
      } else if (e.kind === 'sink') {
        this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.1), { n: 16, color: '#3a3026', speed: 1.4, up: 1.6, size: 0.24, life: 14, gravity: 1 });
      } else if (e.kind === 'slap') {
        this.ctx.shake(0.5);
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.05), 0.4, 2.2, '#8a7058', 8, 0.9);
        this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.1), { n: 20, color: '#4a3b2e', speed: 2.4, up: 1.4, size: 0.24, life: 12, gravity: 1 });
      } else if (e.kind === 'mudWave') {
        this.ctx.shake(0.45);
        for (let i = 0; i < 9; i++) this.ctx.vfx.burst(this.ctx.at(-8000 + i * 2000, e.y + 600, 0.2), { n: 8, color: '#4a3b2e', speed: 2.2, up: 1.6, size: 0.3, life: 14, gravity: 1 });
      }
    }
  }

  draw(s: BattleState): void {
    this.lastState = s;
    this.drawThaw(s);
    this.drawGlade(s);
    this.drawStream(s);
  }

  private drawThaw(s: BattleState): void {
    const fm = this.flood;
    const w = s.senda?.druid;
    if (!fm || !w) return;
    const u = fm.uniforms;
    (u.uOn as THREE.IUniform<number>).value = w.front !== NONE ? 1 : 0;
    (u.uFront as THREE.IUniform<number>).value = w.front !== NONE ? w.front * K : -1e9;
    (u.uLow as THREE.IUniform<number>).value = w.low !== NONE ? w.low * K : 1e9;
    // Spray along the surge's front, a few points a tick.
    if (w.front !== NONE && !this.ctx.low) {
      for (let i = 0; i < 3; i++) {
        const x = -6000 + ((s.tick * 7 + i * 4100) % 12000);
        this.ctx.vfx.burst(this.ctx.at(x, w.front, 0.3), { n: 2, color: '#eef4f2', speed: 1.2, up: 1.4, size: 0.3, life: 10, smoke: true });
      }
    }
  }

  private drawGlade(s: BattleState): void {
    const L = this.Gl;
    const sh = this.shoots;
    const fe = this.fences;
    const w = s.senda?.druid;
    if (!L || !sh || !fe) return;
    let n = 0;
    let f = 0;
    const gr = s.rules?.senda?.druid?.glade;
    const up = gr ? gladeUp(gr, s.waveT) : L.shoots.length;
    L.shoots.forEach((h, i) => {
      const p = this.hex(h.q, h.r);
      const eaten = !!w && !!(w.eaten & (1 << i));
      const fenced = !!w && !!(w.fenced & (1 << i));
      if (i >= up) {
        // Not up yet: a little mound of turned earth where it will come (the clearing keeps sprouting).
        this.m4.compose(this.v3.set(p.x, p.y + 0.02, p.z), this.q4.identity(), this.s3.set(2.2, 0.05, 2.2));
        sh.setMatrixAt(n, this.m4);
        sh.setColorAt(n, this.c3.set('#4a3a2a'));
        n++;
        return;
      }
      if (eaten) {
        // Eaten: a smear of the slime's mud where it stood.
        this.m4.compose(this.v3.set(p.x, p.y + 0.02, p.z), this.q4.identity(), this.s3.set(4, 0.06, 3.4));
        sh.setMatrixAt(n, this.m4);
        sh.setColorAt(n, this.c3.set('#2e241c'));
        n++;
      } else {
        // Three blades of new leaf, leaning out.
        for (let k = 0; k < 3; k++) {
          const a = k * 2.1 + i;
          this.q4.setFromEuler(new THREE.Euler(Math.cos(a) * 0.45, 0, Math.sin(a) * 0.45));
          this.m4.compose(this.v3.set(p.x + Math.cos(a) * 0.08, p.y, p.z + Math.sin(a) * 0.08), this.q4, this.s3.setScalar(0.9 + k * 0.15));
          sh.setMatrixAt(n, this.m4);
          sh.setColorAt(n, this.c3.set(k === 1 ? '#c8f08a' : '#a8e070'));
          n++;
        }
      }
      if (fenced) {
        for (let k = 0; k < 8; k++) {
          const a = (k / 8) * Math.PI * 2;
          this.q4.setFromEuler(new THREE.Euler(Math.sin(a) * 0.12, 0, -Math.cos(a) * 0.12));
          this.m4.compose(this.v3.set(p.x + Math.cos(a) * 0.5, p.y, p.z + Math.sin(a) * 0.5), this.q4, this.s3.set(1, 0.9 + (k % 3) * 0.12, 1));
          fe.setMatrixAt(f++, this.m4);
        }
      }
    });
    sh.count = n;
    fe.count = f;
    sh.instanceMatrix.needsUpdate = true;
    if (sh.instanceColor) sh.instanceColor.needsUpdate = true;
    fe.instanceMatrix.needsUpdate = true;
    // The face shows as Ceda's search goes; white when found.
    if (this.faceMat && w && s.rules?.senda?.druid?.glade) {
      const k = Math.min(1, s.waveT / Math.max(1, s.rules.senda.druid.glade.search));
      this.faceMat.emissive.setRGB(0.12 * k + (w.done >= 0 ? 0.16 : 0), 0.1 * k + (w.done >= 0 ? 0.14 : 0), 0.07 * k + (w.done >= 0 ? 0.1 : 0));
    }
  }

  /** A root's piece from a to b (world points): an arc over the water, `f` of it grown; `twist` round the other root. */
  private piece(a: THREE.Vector3, b: THREE.Vector3, f: number, n: number, k: number, braided: boolean, thick: number, color: THREE.Color): number {
    const roots = this.roots as THREE.InstancedMesh;
    const steps = 5;
    const len = a.distanceTo(b);
    const lift = Math.min(0.7, len * 0.12) + 0.3;
    const pt = (t: number, out: THREE.Vector3): THREE.Vector3 => {
      out.lerpVectors(a, b, t);
      out.y = a.y + (b.y - a.y) * t + Math.sin(t * Math.PI) * lift + 0.32;
      if (braided) {
        const side = Math.sin(t * Math.PI * 4 + k) * 0.12;
        out.x += -(b.z - a.z) / len * side;
        out.z += (b.x - a.x) / len * side;
        out.y += Math.cos(t * Math.PI * 4 + k) * 0.08;
      }
      return out;
    };
    const p0 = new THREE.Vector3();
    const p1 = new THREE.Vector3();
    for (let i = 0; i < steps && n < roots.instanceMatrix.count; i++) {
      const t0 = i / steps;
      if (t0 >= f) break;
      const t1 = Math.min(f, (i + 1) / steps);
      pt(t0, p0);
      pt(t1, p1);
      const d = p0.distanceTo(p1);
      if (d < 1e-3) continue;
      this.v3.subVectors(p1, p0).normalize();
      this.q4.setFromUnitVectors(new THREE.Vector3(0, 1, 0), this.v3);
      const r = thick * (1 - t0 * 0.25);
      this.m4.compose(p0, this.q4, this.s3.set(r, d, r));
      roots.setMatrixAt(n, this.m4);
      roots.setColorAt(n, color);
      n++;
    }
    return n;
  }

  private drawStream(s: BattleState): void {
    const L = this.St;
    const roots = this.roots;
    const w = s.senda?.druid;
    const r = s.rules?.senda?.druid?.roots;
    if (!L || !roots || !w?.roots || !r) return;
    const st = w.roots;
    const weave = w.weave >= 0 ? Math.min(1, (s.tick - w.weave) / Math.max(1, r.weave)) : w.done >= 0 ? 1 : 0;
    let n = 0;
    const base = new THREE.Color('#6e5236');
    const live = new THREE.Color('#86b04e');
    const hot = this.c3.copy(base).lerp(live, 0.5 + weave * 0.5);
    st.tips.forEach((tip, i) => {
      const braided = tip.follow >= 0;
      const thick = 0.3 + weave * 0.2;
      for (let k = 0; k + 1 < tip.trail.length; k++) n = this.piece(this.nodeAt[tip.trail[k] as number] as THREE.Vector3, this.nodeAt[tip.trail[k + 1] as number] as THREE.Vector3, 1, n, i * 1.7, braided && k >= 1, thick, hot);
      if (tip.mode === 1 && tip.to >= 0) {
        const f = Math.max(0, Math.min(1, (s.tick - tip.t0) / Math.max(1, tip.arr - tip.t0)));
        n = this.piece(this.nodeAt[tip.at] as THREE.Vector3, this.nodeAt[tip.to] as THREE.Vector3, f, n, i * 1.7, braided, thick * 0.9, this.c3.copy(live));
      }
    });
    roots.count = n;
    roots.instanceMatrix.needsUpdate = true;
    if (roots.instanceColor) roots.instanceColor.needsUpdate = true;
    if (this.rootMat) this.rootMat.emissiveIntensity = 0.3 + weave * 1.1 + this.bridgeFlash;
    // The sprouts on the stones sown.
    const sp = this.sprouts as THREE.InstancedMesh;
    let m = 0;
    for (let k = 3; k < r.nodes.length; k++) {
      if (!(st.sprout & (1 << k))) continue;
      const p = this.nodeAt[k] as THREE.Vector3;
      for (let j = 0; j < 3; j++) {
        const a = j * 2.1 + k;
        this.q4.setFromEuler(new THREE.Euler(Math.cos(a) * 0.4, 0, Math.sin(a) * 0.4));
        this.m4.compose(this.v3.set(p.x + Math.cos(a) * 0.07, p.y + 0.3, p.z + Math.sin(a) * 0.07), this.q4, this.s3.setScalar(0.9 + j * 0.15));
        sp.setMatrixAt(m++, this.m4);
      }
    }
    sp.count = m;
    sp.instanceMatrix.needsUpdate = true;
    // The rings: the knot (red, pulsing), the slap coming (red, closing), the Lodazal's sprout (soft red).
    const rings = this.rings as THREE.InstancedMesh;
    let q = 0;
    const ring = (x: number, y: number, rad: number, color: THREE.ColorRepresentation, h = 0.08): void => {
      if (q >= 8) return;
      const p = this.ctx.at(x, y, h);
      this.m4.compose(p, this.q4.identity(), this.s3.set(rad, 1, rad));
      rings.setMatrixAt(q, this.m4);
      rings.setColorAt(q, this.c3.set(color));
      q++;
    };
    if (st.knot >= 0) {
      const h = r.nodes[st.knot] as { q: number; r: number };
      const c = hexToWorld(h.q, h.r);
      ring(c.x, c.y, 0.75 + 0.12 * Math.sin(this.now * 6), '#ff4f3a', 0.45);
    }
    if (w.bmode === 5) {
      const k = Math.max(0, Math.min(1, 1 - (w.bt - s.tick) / Math.max(1, r.lodazal.warn)));
      ring(w.bx, w.by, (r.lodazal.slapR / 1000) * S, '#ff4f3a');
      ring(w.bx, w.by, (r.lodazal.slapR / 1000) * S * (1 - k * 0.85), '#ff8a6e');
    }
    if ((w.bmode === 1 || w.bmode === 2) && w.bnode >= 0) {
      const h = r.nodes[w.bnode] as { q: number; r: number };
      const c = hexToWorld(h.q, h.r);
      ring(c.x, c.y, 1.05, w.bmode === 2 ? '#ff4f3a' : '#ff8a6e', 0.36);
      if (s.tick % 3 === 0) this.ctx.vfx.burst(this.ctx.at(c.x, c.y, 0.2), { n: 3, color: '#4a3b2e', speed: 0.8, up: 1.2, size: 0.16, life: 10, gravity: 1 });
    }
    rings.count = q;
    rings.instanceMatrix.needsUpdate = true;
    if (rings.instanceColor) rings.instanceColor.needsUpdate = true;
    // The mud wave coming: the band over the bank darkens as it nears.
    if (this.mudBand && this.mudMat) {
      const on = w.wave >= 0;
      this.mudBand.visible = on;
      if (on) this.mudMat.opacity = 0.25 + 0.45 * Math.max(0, Math.min(1, 1 - (w.wave - s.tick) / Math.max(1, r.wave.warn)));
    }
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    if (this.flood) (this.flood.uniforms.uTime as THREE.IUniform<number>).value = now;
    for (const m of this.water) (m.uniforms.uTime as THREE.IUniform<number>).value = now;
    if (this.rain && camera) this.rain.frame(now, lookPoint(camera, this.look));
    this.bridgeFlash = Math.max(0, this.bridgeFlash - dt * 0.5);
    // The opening's leaf: down from the crown in slow turns, then still on the flags.
    if (this.leaf) {
      const t = Math.min(1, now / 11);
      const g = this.ctx.ground(0.3, 0.5);
      this.leaf.position.set(0.3 + Math.sin(now * 1.6) * 0.9 * (1 - t), g + 0.03 + (1 - t) * 6.5, 0.5 + Math.cos(now * 1.3) * 0.5 * (1 - t));
      this.leaf.rotation.set(Math.sin(now * 2.1) * 0.8 * (1 - t), now * 1.2 * (1 - t), Math.cos(now * 1.7) * 0.6 * (1 - t));
    }
    // The mud pools bubble now and then.
    if (this.poolAt.length && now > this.bubble) {
      this.bubble = now + 0.6;
      const p = this.poolAt[Math.floor((now * 3.7) % this.poolAt.length)] as THREE.Vector3;
      this.ctx.vfx.burst(p.clone().setY(p.y + 0.1), { n: 3, color: '#4a3b2e', speed: 0.4, up: 0.8, size: 0.14, life: 10, gravity: 1 });
    }
    this.glow.begin();
    for (const l of this.lights) {
      const f = l.flick ? 0.86 + 0.14 * Math.sin(now * (3 + l.seed) + l.seed) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * f);
    }
    // The mounds' rims while a surge is coming: the safe ground, lit pale.
    if (now < this.warnUntil) for (const m of this.moundAt) this.glow.add(m.x, m.y + 0.1, m.z, PALE, 2.6, 0.32 + 0.18 * Math.sin(now * 5));
    const s = this.lastState;
    // The shoots' glow in the clearing; the sprouts' on the stones.
    if (this.Gl && s) {
      const w = s.senda?.druid;
      this.Gl.shoots.forEach((h, i) => {
        if (w && w.eaten & (1 << i)) return;
        const p = this.hex(h.q, h.r, 0.4);
        this.glow.add(p.x, p.y, p.z, GREEN, 1.1, w && w.fenced & (1 << i) ? 0.3 : 0.5);
      });
      if (this.faceAt && w) {
        const k = s.rules?.senda?.druid?.glade ? Math.min(1, s.waveT / Math.max(1, s.rules.senda.druid.glade.search)) : 1;
        this.glow.add(this.faceAt.x, this.faceAt.y, this.faceAt.z + 0.2, PALE, 1.5 + k * 1.5, 0.15 + k * 0.4);
      }
    }
    if (this.St && s) {
      const st = s.senda?.druid?.roots;
      if (st) {
        for (let k = 3; k < this.nodeAt.length; k++) {
          if (!(st.sprout & (1 << k))) continue;
          const p = this.nodeAt[k] as THREE.Vector3;
          this.glow.add(p.x, p.y + 0.6, p.z, GREEN, 1.3, 0.55);
        }
        // The growing tips glow; a knot burns red.
        st.tips.forEach((tip) => {
          const p = this.nodeAt[tip.mode === 1 ? tip.to : tip.at] as THREE.Vector3 | undefined;
          if (p && tip.mode === 1) this.glow.add(p.x, p.y + 0.5, p.z, PALE, 0.9, 0.35 + 0.2 * Math.sin(now * 4));
        });
        if (st.knot >= 0) {
          const p = this.nodeAt[st.knot] as THREE.Vector3;
          this.glow.add(p.x, p.y + 0.6, p.z, RED, 1.8, 0.55 + 0.25 * Math.sin(now * 6));
        }
      }
      const isle = this.nodeAt[2];
      if (isle && this.bridgeFlash > 0) this.glow.add(isle.x, isle.y + 1, isle.z, GREEN, 4 + this.bridgeFlash * 6, this.bridgeFlash);
    }
    this.glow.end();
  }

  /** The stones and the knot in world points (the HUD's marks hang over them): the net's nodes. */
  stonePoints(): THREE.Vector3[] {
    return this.nodeAt.map((p) => p.clone());
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.rain?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}
